import {
  Entry,
  DirectoryNode,
  TreeNode,
  ScanParam,
  Sorter,
  SortingOption,
} from "common/types";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import crypto from "crypto";

dayjs.extend(relativeTime);
dayjs.extend(duration);

export const DEFAULT_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";

export function formatDate(
  date: string | number,
  format = DEFAULT_DATE_TIME_FORMAT
) {
  return dayjs(date).format(format);
}

export const toNowDate = (date: string) => {
  return dayjs().to(formatDate(date));
};

export const parseDate = (date: string) => dayjs(date).toDate();

export const serializeSorter = <T>({ sorter }: SortingOption<T>) =>
  `${sorter.property},${sorter.ascending ? "asc" : "desc"}`;

export const string2number = (str: string): number | undefined => {
  const value = parseInt(str);
  if (isNaN(value)) return undefined;
  return value;
};

export const formatNumber = (num: number) => {
  if (num < 1000) {
    return num.toFixed(0);
  } else {
    return (num / 1000).toFixed(1) + "K";
  }
};

export const formatDurationMillis = (duration: number) => {
  const dur = Math.max(1000, duration); // 不足1s显示1s
  const beforeFormatDuration = dayjs.duration(dur, "milliseconds");
  let formatString = "";
  if (beforeFormatDuration.asDays() >= 1) {
    formatString = "D[d] H[h]";
  } else if (beforeFormatDuration.asHours() >= 1) {
    formatString = "H[h] m[m]";
  } else if (beforeFormatDuration.asMinutes() >= 1) {
    formatString = "m[m] s[s]";
  } else {
    formatString = "s[s]";
  }
  return beforeFormatDuration.format(formatString).replace(/\b0[dhms]\b/g, "");
};

export const formatDurationBetween = (startTime: string, endTime: string) => {
  const diffMillis = dayjs(endTime).diff(dayjs(startTime), "milliseconds");
  return formatDurationMillis(diffMillis);
};

export const markdownLinkFromat = (
  mdStr: string,
  baseUrl: string,
  sub_path?: string
) => {
  const linkReg = /\[(.*?)\]\((.*?)\)/gi; // (?<!!) 不做图片格式的字符串过滤
  if (!sub_path) return mdStr.replace(linkReg, `[$1](${baseUrl}/$2)`);

  return mdStr.replace(linkReg, `[$1](${baseUrl}/${sub_path}/$2)`);
};

export function formatFileSize(size: number) {
  if (size < 1024) {
    return size + " bytes";
  } else if (size >= 1024 && size < 1048576) {
    return (size / 1024).toFixed(1) + " KB";
  } else if (size >= 1048576) {
    return (size / 1048576).toFixed(1) + " MB";
  }
}

export const formatSourceLine = (
  isGithub: boolean,
  baseHref: string,
  line_start: string,
  line_end: string
) => {
  return isGithub
    ? `${baseHref}#L${line_start}-#L${line_end}`
    : `${baseHref}#L${line_start}-${line_end}`;
};

export const formatGithubAddress = ({
  baseUrl = "",
  scanParams,
}: {
  baseUrl: string | undefined;
  scanParams: ScanParam | undefined;
}) => {
  return `${baseUrl}/blob/${
    scanParams?.repo?.commit_hash || scanParams?.repo.branch || ""
  }`;
};

export function generateHash(str: string) {
  const hash = crypto.createHash("sha256");
  hash.update(str);
  return hash.digest("hex");
}

export function transformData(data: Entry[]): TreeNode {
  const result: DirectoryNode = { type: "directory", name: ".", contents: [] };
  let directoriesCount = 0;

  data.forEach((entry) => {
    const parts = entry.filename.split("/").filter((item) => item !== ".");
    let currentDirectory: DirectoryNode = result;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (i === parts.length - 1) {
        if (entry.directory) {
          directoriesCount++;
          currentDirectory.contents.push({
            type: "directory",
            name: part,
            contents: [],
          });
        } else {
          currentDirectory.contents.push({ type: "file", name: part });
        }
      } else {
        let subDirectory = currentDirectory.contents.find(
          (dir) => dir.type === "directory" && dir.name === part
        ) as DirectoryNode | undefined;
        if (!subDirectory) {
          directoriesCount++;
          subDirectory = { type: "directory", name: part, contents: [] };
          currentDirectory.contents.push(subDirectory);
        }
        currentDirectory = subDirectory;
      }
    }
  });
  return result;
}
